import { authClient, getAuthProvider, setAuthTransitionDeadline, setServerAuthPolicy } from '@40seas/auth-client';
import { AuthProvider } from '@40seas/entities/auth';
import { CurrentUser } from '@40seas/frontend/libs/entities';
import { useSetupData } from '@40seas/frontend/libs/hooks';
import { datadogRumProvider, useFeatureToggleClient } from '@40seas/frontend/libs/providers';
import { UsersService } from '@40seas/frontend/libs/services';
import jwt_decode from 'jwt-decode';
import * as React from 'react';
import { useAuth } from './useAuth';

export const useCurrentUserNew = () => {
    const setup = useSetupData();
    const apiUrl = setup.urls.API_URL.endsWith('/') ? `${setup.urls.API_URL}v1` : `${setup.urls.API_URL}/v1`;

    const { getAccessTokenSilently } = useAuth();

    const { identify } = useFeatureToggleClient();

    const [isLoading, setLoading] = React.useState(false);
    const [user, setUser] = React.useState<CurrentUser | null>(null);

    const fetchUser = async () => {
        setLoading(true);
        const accessToken = await getAccessTokenSilently();
        const userService = new UsersService(apiUrl, { Authorization: `Bearer ${accessToken}` });

        try {
            const user = await userService.getOrRegister(getToken());

            if (user.authProvider) {
                // Check mismatch BEFORE storing, so the cached provider
                // is compared against the old value even on re-render.
                const needsTransition = user.authProvider !== getAuthProvider();
                setServerAuthPolicy(user.authProvider);
                if (needsTransition) {
                    const jwt = jwt_decode<{ exp: number }>(accessToken);
                    setAuthTransitionDeadline(jwt.exp);
                }
            }

            userService.initializeUserTrackers(user);

            identify({
                key: 'dashboard',
                email: user.email,
                name: user.fullName,
                id: user.auth0Id,
            });

            const jwt = jwt_decode<{ permissions: string[] }>(accessToken);
            const isSelfManagedAuth = getAuthProvider() === AuthProvider.SelfManaged;
            const isSelfManagedTokenExpired = authClient.isTokenExpired();

            if ((!isSelfManagedAuth && !jwt.permissions?.length) || (isSelfManagedAuth && isSelfManagedTokenExpired)) {
                await getAccessTokenSilently({ ignoreCache: true });
            }

            setUser(user);
        } catch (error) {
            console.error(error);
            datadogRumProvider.reportToDatadog(error);
        } finally {
            setLoading(false);
        }
    };

    return { user, isLoading, fetchUser };
};

const getToken = (): string => {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;

    return searchParams.get('t') || '';
};
